import React, { useCallback, useEffect, useMemo, useState, Dispatch } from 'react';
import styles from './Schools.module.scss';
import BadgePillGroup, { BadgePillItem } from '@src/components/badge-pill-group';
import { useHistory, useLocation } from 'react-router-dom';
import { Customer, SelectOption } from '../../../types';
import Button from '../../../components/button/Button';
import { BodyColumn, HeaderColumn } from '../../../components/table/Table';
import Spinner from '../../../components/spinner/Spinner';
import AddModal from './add-modal/AddModal';
import { School, useAccountSchoolsLazyQuery, useDeleteSchoolMutation } from '../../../api/graphql-codegen';
import { useQuerySllProgramWithCountry } from '@src/hooks';
import { auAllYearLevels, countries, countryOptions, IconInfo, naAllGradeLevels } from '@src/constants';
import NormalModal from '@src/components/modal/normal-modal/NormalModal';
import { TextInput } from '@src/components/text-input/TextInput';
import IconWarning from '@src/assets/icons/modal-warning.svg?react';
import { LordIcon } from '@src/components/lord-icon/lord-icon';
import { AdminGridWithBackButton, AdminTable, ActionButtons } from '@src/components/admin-grid';
import { useToast } from '@src/hooks/useToast';
import FilterSection from '@src/components/filter-section';
import SentryTrackingButton from '@src/components/sentry-tracking/SentryTrackingButton';

type Params = {
  customer?: Customer;
};

const statusOptions = [
  { label: 'Active', value: 'active' },
  { label: 'Expired', value: 'expired' }
]

function SuperAccountSchools() {
  const history = useHistory<Params>();
  const { state } = useLocation<{ successSchoolId?: string }>();
  const successSchoolId = state?.successSchoolId;
  const [customer, setCustomer] = useState<Customer | undefined>(history.location.state?.customer);
  const [isReset, setIsReset] = useState(false);

  useEffect(() => {
    // If customer is not in location state, extract account ID and name from URL
    if (!customer) {
      const pathParts = window.location.pathname.split('/');
      const encodedAccountId = pathParts[pathParts.indexOf('accounts') + 1];
      
      if (encodedAccountId) {
        // Decode the URL-encoded account ID
        const accountId = decodeURIComponent(encodedAccountId);
        
        // Get account name from query parameter
        const urlParams = new URLSearchParams(window.location.search);
        const accountName = urlParams.get('accountName');
        
        // Create customer object with ID and name (if available)
        setCustomer({ 
          id: accountId,
          name: accountName ? decodeURIComponent(accountName) : undefined
        } as Customer);
      } else {
        // If no account ID in URL, redirect to accounts page
        history.push('/super/accounts');
      }
    }
  }, [history, customer]);

  const [accountSchools, {data, loading, refetch}] = useAccountSchoolsLazyQuery({fetchPolicy: 'network-only', 
    onCompleted(data) {
      if (data && data.accountSchools?.schools && data.accountSchools.schools.length > 0) {
        setIsReset(false);
      }
    },
  });
  const programOptions = useQuerySllProgramWithCountry();

  useEffect(() => {
    if (customer?.id) {
      accountSchools({
        variables: {
          input: {
            id: customer?.id
          }
        }
      });
    }
  },[accountSchools, customer?.id]);

  const [showAddModal, setShowAddModal] = useState(false);
  const [search, setSearch] = useState('');
  const [group, setGroup] = useState('');
  const [dataCollection, setDataCollection] = useState('');
  const [program, setProgram] = useState('');
  const [grade, setGrade] = useState('');
  const [status, setStatus] = useState('');

  const [school, setSchool] = useState<School | undefined>(undefined);
  const [showDeletModal, setShowDeleteModal] = useState(false);
  const [confirmDeletModal, setConfirmDeleteModal] = useState(false);
  const [deleteText, setDeletText] = useState('');

  const [deleteSchool, {loading: deleting}] = useDeleteSchoolMutation();
  const {successToast} = useToast();

  const handleCopyToClipboard = useCallback((text: string) => {
    successToast(text);
  }, [successToast]);

  useEffect(() => {
    if (successSchoolId && !loading) {
      const accountSchools = data?.accountSchools?.schools;
      if (accountSchools) {
        const selectedSchool = accountSchools.find(s => s?.id === successSchoolId);
        if (selectedSchool) {
          setSchool(selectedSchool as School);
          setShowAddModal(true);
        }
      }
    }
  }, [successSchoolId, loading, data]);

  const sllProgramList = useMemo(() => {
    return programOptions.filter(p => p.country.includes(countries[customer?.country ?? ''])).map(p => {
      return {
        label: p.label,
        value: p.value
      }}
    )
  }, [programOptions, customer?.country])

  const filter = useMemo(() => {
    if (!data?.accountSchools?.schools) return [];
    
    // If isReset is true, return an empty array
    if (isReset) return [];
    
    const query = (search ?? '').toLowerCase();
    let s: School[] = data.accountSchools.schools.filter(s => !!s) as School[];
    if (query) {
      s = s.filter(row => row?.name.toLowerCase().includes(query));
    }

    if (group) {
      s = s.filter(s => s.groups?.map(g => g?.id).includes(group));
    }
    if (program) {
      s = s.filter(s => JSON.parse(s.programs || '[]')?.map(g => g).includes(program));
    }
    if (grade) {
      s = s.filter(s => JSON.parse(s.grades || '[]')?.map(g => g).includes(grade));
    }
    if (dataCollection) {
      s = s.filter(s => s.dataCollections?.map(d => d?.id).includes(dataCollection));
    }
    if (status) {
      s = s.filter(d => d.status === status);
    }
    s.sort((a, b) => a.name > b.name ? 1 : -1);

    return s;
  }, [data?.accountSchools?.schools, isReset, search, group, program, grade, dataCollection, status]);

  const handleOpenAddNewModal = () => {
    setShowAddModal(true);
  };

  const handleCloseModal = () => {
    refetch();
    setShowAddModal(false);
    setShowDeleteModal(false);
    setSchool(undefined);
  };

  const handleChangeSearch = (value: string) => setSearch(value);
  const handleChangeDelText = (value: any) => setDeletText(value);

  const handleEdit = useCallback((row: School) => () => {
    setSchool(row);
    handleOpenAddNewModal();
  }, []);

  const handleDelete = useCallback((row: School) => () => {
    setSchool(row);
    setShowDeleteModal(true);
  }, []);

  const handleConfirmRemove = useCallback(() => {
    setShowDeleteModal(false);
    setConfirmDeleteModal(true);
    setDeletText('');
  }, [])

  const handleRemoveSchool = useCallback(async() => {
    if (school?.id) {
      const response = await deleteSchool({
        variables: {
          input: {
            id: school?.id
          }
        }
      })
      if (response.data?.deleteSchool) {
        successToast('School successfully deleted!');
        setConfirmDeleteModal(false);
        refetch();
      }
    }
  }, [school?.id, deleteSchool, refetch, successToast])

  const handleRefetch = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleResetFilters = () => {
    // Reset all filter states
    setGroup('');
    setSearch('');
    setDataCollection('');
    setGrade('');
    setProgram('');
    
    // Set the reset flag to true to show empty results
    setIsReset(true);
  };

  const handleChangeFilter = useCallback((setter: Dispatch<string>) => (o: SelectOption) => {
    setter(o.value);
  }, []);

  const headerColumns: HeaderColumn[] = useMemo(() => {
    return [
      {title: 'Name'},
      {title: 'Groups', center: true},
      {title: 'Program', center: true},
      {title: 'Age Group', center: true},
      {title: 'Data Collection', center: true},
      {title: 'Status', center: true},
      {title: 'Edit', center: true},
    ];
  }, []);

  const bodyColumns: BodyColumn[] = useMemo(() => {
    return [
      {
        key: (row: School) => row.name
      },
      {
        key: (row: School) => {
          const groups = row.groups || [];
          const groupItems: BadgePillItem[] = groups.map(g => ({
            id: g?.id || '',
            label: g?.name || ''
          }));
          
          return <BadgePillGroup items={groupItems} />;
        }
      },
      {
        key: (row: School) => {
          const schoolPrograms = JSON.parse(row.programs || '[]');
          const programNames = schoolPrograms?.map(prog => 
            sllProgramList.find(option => option.value === prog)?.label
          ).filter(c => !!c);
          
          const programItems: BadgePillItem[] = programNames.map((name, index) => ({
            id: `program-${index}`,
            label: name
          }));
          
          return <BadgePillGroup items={programItems} />;
        }
      },
      {
        key: (row: School) => {
          const schoolGrades = JSON.parse(row.grades || '[]');
          
          const gradeItems: BadgePillItem[] = schoolGrades.map((grade, index) => ({
            id: `grade-${index}`,
            label: grade
          }));
          
          return <BadgePillGroup items={gradeItems} />;
        }
      },
      {
        key: (row: School) => {
          const dataCollections = row.dataCollections || [];
          const dataCollectionItems: BadgePillItem[] = dataCollections.map(d => ({
            id: d?.id || '',
            label: d?.name || ''
          }));
          
          return <BadgePillGroup items={dataCollectionItems} />;
        }
      },
      {
        key: (row: School) => row.status
      },
      {
        key: (row: School, column, options) => (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <ActionButtons
              onEdit={handleEdit(row)}
              editTooltip="Edit school"
              onDelete={handleDelete(row)}
              deleteTooltip="Delete school"
              onCopy={handleCopyToClipboard}
              copyText={`${window.location.protocol + "//" + window.location.host}/registration?id=${row.id}&entity=sch`}
              copyTooltip="Click to copy registration link for the school"
              showView={false}
              isRowHovered={options?.isRowHovered}
            />
            <SentryTrackingButton 
              entityId={row.id}
              entityType="school"
              entityName={row.name}
              buttonText="Track"
              buttonSize="small"
              onTrackingAdded={handleRefetch}
              isRowHovered={options?.isRowHovered}
            />
          </div>
        )
      }
    ]
  }, [sllProgramList, handleEdit, handleDelete, handleCopyToClipboard, handleRefetch]);

  const hasData = !loading && data?.accountSchools?.schools?.length !== 0;

  // Custom subtitle component
  const subtitleComponent = (
    <div className={styles.subtitle}>for <span>{customer?.name}</span></div>
  );

  // Filter section component using the reusable FilterSection component
  const filterSection = hasData && (
    <FilterSection
      searchValue={search}
      onSearchChange={handleChangeSearch}
      hasSearchInput={true}
      showDivider={false}
      filters={[
        {
          value: group,
          onChange: handleChangeFilter(setGroup),
          label: 'Group',
          options: (data?.accountSchools?.groups || []).map(g => ({ label: g?.name || '', value: g?.id || '' }))
        },
        {
          value: program,
          onChange: handleChangeFilter(setProgram),
          label: 'Program',
          options: sllProgramList
        },
        {
          value: grade,
          onChange: handleChangeFilter(setGrade),
          label: 'Age Group',
          options: customer?.country === countryOptions[1].value ? auAllYearLevels : naAllGradeLevels,
          doNotSort: true
        },
        {
          value: dataCollection,
          onChange: handleChangeFilter(setDataCollection),
          label: 'Data Collection',
          options: (data?.accountSchools?.dataCollections || []).map(d => ({ label: d?.name || '', value: d?.id || '' }))
        },
        {
          value: status,
          onChange: handleChangeFilter(setStatus),
          label: 'Status',
          options: statusOptions
        }
      ]}
      onReset={handleResetFilters}
      hideResetButton={true}
    />
  );

  // Header actions component
  const headerActions = hasData && (
    <Button 
      className={styles.addButton} 
      size={'small'} 
      label={'ADD NEW'} 
      onClick={handleOpenAddNewModal}
    />
  );

  return (
    <AdminGridWithBackButton
      title="Schools"
      subtitle={subtitleComponent}
      filterSection={filterSection}
      headerActions={headerActions}
      resultsCount={filter.length}
      resultsCountLabel="Schools"
      backButtonLink="/super/accounts"
    >
      {hasData || loading ? (
        loading ? (
          <div className={styles.spinnerContainer}>
            <Spinner/>
          </div>
        ) : (
          <AdminTable
            header={headerColumns}
            body={bodyColumns}
            data={filter}
            color="green"
            noItemsText="No schools found."
          />
        )
      ) : (
        <div className={styles.hasNoDataWrapper}>
          <div className={styles.hasNoData}>
            <div className={styles.hasNoDataText}>
              <div>The list is empty for now.</div>
              <div>You can add a school by clicking</div>
            </div>
            <Button onClick={handleOpenAddNewModal} className={styles.inviteBtn} label={'ADD NEW'} size={'small'}/>
          </div>
        </div>
      )}
      {showAddModal && <AddModal account={customer!} open={showAddModal} onClose={handleCloseModal} school={school} programOptions={sllProgramList}/>}
      {showDeletModal && 
        <NormalModal
          logoIcon={<IconWarning />} title={'WARNING?'} open={showDeletModal} onClose={handleCloseModal}
          description={<div className={styles.detail}>This will permanently delete all Schools, Users, and Analytics in <strong>{school?.name}</strong>. This cannot be undone.</div>}
          leftButton={<Button size='small' color='green' className={styles.cancelButton} disabled={false} onClick={handleCloseModal} label={'CANCEL'}/>}
          rightButton={<Button size='small' color='red' className={styles.resetButton} disabled={false} onClick={handleConfirmRemove} label={'DELET ACCOUNT'} />}
        />
      }
      {confirmDeletModal &&
        <NormalModal 
          logoIcon={<IconWarning className={styles.redWarning} />} title={<div className={styles.redWarningTitle}>CONFIRM DELETION</div>} open={confirmDeletModal} onClose={()=>{setConfirmDeleteModal(false)}} loading={deleting}
          description={
            <div className={styles.redDetail}>
              Please type the characters "DELETE" to remove <strong>{school?.name}</strong>. This cannot be undone.
              <TextInput className={styles.deleteInput} id='deleteInput' label='' value={deleteText} placeholder='Please type the characters "DELETE"' onChange={handleChangeDelText} />
            </div>
          }
          leftButton={<Button size='small' color='green' className={styles.cancelButton} disabled={deleting} onClick={()=>{setConfirmDeleteModal(false)}} label={'CANCEL'}/>}
          rightButton={<Button size='small' color='red' className={styles.resetButton} disabled={deleting || deleteText !== 'DELETE'} onClick={handleRemoveSchool} label={'DELETE ACCOUNT'} rightIcon={<div className={styles.infoIcon}><LordIcon src={IconInfo} isHover={true} colors={{primary: '#FFFFFF', secondary: '#FFFFFF'}} size={23.4}/></div>}/>}
        />
      }
    </AdminGridWithBackButton>
  );
}

export default SuperAccountSchools;
